/**
 * Connectivity run fields (#283).
 *
 * A connectivity run is a test like any other — you pick it in the same Flow picker, at the
 * same location, from the same New Test Run sheet. It just runs for days instead of seconds,
 * has no pass/fail, and holds the modems' registration while it does. Everything below is the
 * part of the form that only applies to one, rendered in place of eSIM / network constraints /
 * timing, which a connectivity run doesn't have.
 *
 * This used to be a separate "New Campaign" button opening a separate sheet with its own Name
 * and Where fields — two nouns and two entry points for one concept, which is what the user
 * pushed back on. The fields moved here; the chrome is shared.
 */
import React, { useEffect, useMemo, useState } from 'react';
import type { CampaignConfig, RecurrenceUnit } from '@simcheck/shared-types';
import {
  Input, Label, Switch, Badge, Textarea,
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui';
import { getTestConfigurations } from '@/services/testConfigurationService';

/**
 * Synthetic flow ids. A connectivity run has no flow document — the Pi runs the mode, not a
 * node graph — so the picker needs an id that can never collide with a real `flows/{id}`.
 */
export const CONNECTIVITY_FLOW_PREFIX = '__connectivity__:';

export type ConnectivityMode = 'persistent' | 'hunt';

export const CONNECTIVITY_OPTIONS: Array<{
  id: string; mode: ConnectivityMode; name: string; hint: string;
}> = [
  {
    id: `${CONNECTIVITY_FLOW_PREFIX}persistent`,
    mode: 'persistent',
    name: 'Persistent Attach',
    hint: 'Hold the SIM attached with a warm bearer',
  },
  {
    id: `${CONNECTIVITY_FLOW_PREFIX}hunt`,
    mode: 'hunt',
    name: 'PLMN Hunt',
    hint: 'Scan for target networks and attach when seen',
  },
];

/** `null` for every ordinary flow — callers use it as the "is this a connectivity run" test. */
export function parseConnectivityMode(flowId?: string | null): ConnectivityMode | null {
  if (!flowId || !flowId.startsWith(CONNECTIVITY_FLOW_PREFIX)) return null;
  const mode = flowId.slice(CONNECTIVITY_FLOW_PREFIX.length);
  return mode === 'persistent' || mode === 'hunt' ? mode : null;
}

export interface ConnectivityDraft {
  apn: string;
  probeIntervalSec: number;
  pcap: boolean;
  diagEnabled: boolean;
  diagMask: 'signaling' | 'full' | 'deep';
  windowMinutes: number;
  retentionDays: number;
  onAttachTestConfigId: string;
  onAttachDebounceSec: number;
  onAttachMaxPerHour: number;
  // hunt
  targetPlmns: string;
  lteBands: string;
  nrBands: string;
  scanIntervalSec: number;
  scanMethod: 'qscan' | 'cops';
  attachPolicy: 'on_detect' | 'always_in_window';
  attachTimeoutSec: number;
  holdSec: number;
  idlePolicy: 'detached' | 'terrestrial_auto';
  // schedule
  scheduleKind: 'always' | 'windows' | 'rule';
  recurInterval: number;
  recurUnit: Exclude<RecurrenceUnit, 'pass_windows'>;
  recurCron: string;
  windowDurationMin: number;
  horizonDays: number;
  windowsCsv: string;
  // lifetime
  endMode: 'until_stopped' | 'after' | 'at';
  durationHours: number;
  endsAtLocal: string;
}

export const EMPTY_CONNECTIVITY: ConnectivityDraft = {
  apn: 'internet', probeIntervalSec: 60,
  pcap: true, diagEnabled: true, diagMask: 'signaling',
  windowMinutes: 5, retentionDays: 90,
  onAttachTestConfigId: '', onAttachDebounceSec: 0, onAttachMaxPerHour: 10,
  targetPlmns: '', lteBands: '', nrBands: '', scanIntervalSec: 60, scanMethod: 'qscan',
  attachPolicy: 'on_detect', attachTimeoutSec: 120, holdSec: 300, idlePolicy: 'detached',
  scheduleKind: 'always', recurInterval: 1, recurUnit: 'days', recurCron: '',
  windowDurationMin: 20, horizonDays: 7, windowsCsv: '',
  endMode: 'until_stopped', durationHours: 24, endsAtLocal: '',
};

const parseBands = (v: string) => v.split(/[\s,;]+/).map((x) => Number(x)).filter((n) => Number.isFinite(n) && n > 0);

const num = (v: string, d: number) => (Number.isFinite(Number(v)) && v !== '' ? Number(v) : d);

/** Draft → the wire shape the campaign API takes. */
export function buildCampaignConfig(d: ConnectivityDraft, mode: ConnectivityMode): CampaignConfig {
  const isHunt = mode === 'hunt';
  const connectivityConfig: any = {
    apn: d.apn.trim() || 'internet',
    probe: { intervalSec: d.probeIntervalSec, targets: undefined },
    capture: {
      uploadMode: 'events', windowMinutes: d.windowMinutes,
      pcap: d.pcap, diagEnabled: d.diagEnabled, diagMask: d.diagMask,
    },
    triggers: { detach: true, attachFail: true, probeFail: 3, reconnect: true },
    retentionDays: d.retentionDays,
    rfOffset: null,
    onAttachTestConfigId: d.onAttachTestConfigId || null,
    onAttachDebounceSec: d.onAttachDebounceSec,
    onAttachMaxPerHour: d.onAttachMaxPerHour,
  };
  if (isHunt) {
    connectivityConfig.hunt = {
      targetPlmns: d.targetPlmns.split(/[\s,;]+/).map((x) => x.replace(/\D/g, '')).filter(Boolean),
      bands: {
        lte: parseBands(d.lteBands),
        nr: parseBands(d.nrBands),
      },
      // 'always' keeps the edge's continuous mode; the other two concentrate in windows.
      scanMode: d.scheduleKind === 'always' ? 'auto' : 'scheduled',
      scanIntervalSec: d.scanIntervalSec,
      scanMethod: d.scanMethod,
      attachPolicy: d.attachPolicy,
      attachTimeoutSec: d.attachTimeoutSec,
      holdSec: d.holdSec,
      idlePolicy: d.idlePolicy,
      windowLeadSec: 60,
      windowLagSec: 60,
    };
  }

  const schedule: CampaignConfig['schedule'] = !isHunt || d.scheduleKind === 'always'
    ? { kind: 'always' }
    : d.scheduleKind === 'windows'
      ? { kind: 'windows' }
      : {
          kind: 'rule',
          recurrence: d.recurCron.trim()
            ? ({ interval: 1, unit: 'days', cronExpression: d.recurCron.trim() } as any)
            : ({ interval: d.recurInterval, unit: d.recurUnit } as any),
          durationSec: Math.max(60, d.windowDurationMin * 60),
          horizonDays: d.horizonDays,
        };

  const passWindows = d.scheduleKind === 'windows'
    ? d.windowsCsv.split('\n').map((l) => l.trim()).filter(Boolean).map((l) => {
        const [start, end, satelliteId] = l.split(',').map((x) => x.trim());
        return { start: new Date(start), end: new Date(end), satelliteId: satelliteId || null };
      }).filter((w) => !Number.isNaN(w.start.getTime()) && !Number.isNaN(w.end.getTime()))
    : undefined;

  const endsAt = d.endMode === 'at' && d.endsAtLocal ? new Date(d.endsAtLocal) : null;
  const maxDurationSec = d.endMode === 'after' ? Math.max(60, Math.round(d.durationHours * 3600)) : null;

  return { mode, connectivityConfig, schedule, passWindows, endsAt, maxDurationSec } as CampaignConfig;
}

/** Returns a message when the draft can't be started, else null. */
export function validateConnectivity(d: ConnectivityDraft, mode: ConnectivityMode): string | null {
  if (mode === 'hunt' && d.scheduleKind !== 'always' && d.targetPlmns.replace(/\D/g, '') === '') {
    return 'A PLMN hunt needs at least one target network (MCCMNC)';
  }
  return null;
}

export const ConnectivityFields: React.FC<{
  mode: ConnectivityMode;
  draft: ConnectivityDraft;
  onChange: (next: ConnectivityDraft) => void;
  organizationId: string;
}> = ({ mode, draft: d, onChange, organizationId }) => {
  const [configs, setConfigs] = useState<Array<{ id: string; name: string }>>([]);
  const isHunt = mode === 'hunt';
  const set = <K extends keyof ConnectivityDraft>(k: K, v: ConnectivityDraft[K]) =>
    onChange({ ...d, [k]: v });

  useEffect(() => {
    if (!organizationId) return;
    getTestConfigurations(organizationId)
      .then((cs: any[]) => setConfigs(
        (cs || [])
          // The on-attach test must not itself force a RAT/PLMN or pick a SIM — that would
          // fight the registration this run is holding.
          .filter((c) => !c.forcedRat && !c.forcedNetworkMccMnc && c.kind !== 'connectivity_campaign')
          .map((c) => ({ id: c.id, name: c.name })),
      ))
      .catch(() => setConfigs([]));
  }, [organizationId]);

  const scheduleSummary = useMemo(() => {
    if (!isHunt) return null;
    if (d.scheduleKind === 'always') return 'Scans continuously on its interval — no windows.';
    if (d.scheduleKind === 'windows') return 'Concentrates inside the windows you paste below. They are not renewed, so upload more before they run out.';
    // "every 1 days" reads like a bug report; say "every day".
    const unit = d.recurInterval === 1 ? d.recurUnit.replace(/s$/, '') : `${d.recurInterval} ${d.recurUnit}`;
    const every = d.recurCron.trim() ? `cron "${d.recurCron.trim()}"` : `every ${unit}`;
    const ahead = d.horizonDays === 1 ? '1 day' : `${d.horizonDays} days`;
    return `Generates a ${d.windowDurationMin}-minute window ${every}, kept ${ahead} ahead.`;
  }, [isHunt, d.scheduleKind, d.recurCron, d.recurInterval, d.recurUnit, d.windowDurationMin, d.horizonDays]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>APN</Label>
          <Input value={d.apn} onChange={(e) => set('apn', e.target.value)} />
        </div>
        <div>
          <Label>Probe interval (s)</Label>
          <Input type="number" value={d.probeIntervalSec} onChange={(e) => set('probeIntervalSec', num(e.target.value, 60))} />
        </div>
      </div>

      {isHunt && (
        <>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Target networks (MCCMNC)</Label>
              <Input value={d.targetPlmns} onChange={(e) => set('targetPlmns', e.target.value)} placeholder="313100, 310410" />
            </div>
            <div>
              <Label>LTE bands</Label>
              <Input value={d.lteBands} onChange={(e) => set('lteBands', e.target.value)} placeholder="5, 8, 12" />
            </div>
            <div className="col-start-2">
              <Label>5G NR bands</Label>
              <Input value={d.nrBands} onChange={(e) => set('nrBands', e.target.value)} placeholder="41, 71" />
            </div>
          </div>

          <div>
            <Label>Schedule</Label>
            <Select value={d.scheduleKind} onValueChange={(v) => set('scheduleKind', v as ConnectivityDraft['scheduleKind'])}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="always">Always — scan continuously</SelectItem>
                <SelectItem value="rule">Recurring — generate windows on a schedule</SelectItem>
                <SelectItem value="windows">Uploaded windows — paste exact times</SelectItem>
              </SelectContent>
            </Select>
            {scheduleSummary && <p className="text-caption text-muted-foreground mt-1">{scheduleSummary}</p>}
          </div>

          {d.scheduleKind === 'rule' && (
            <div className="space-y-3 rounded-md border p-3">
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Every</Label>
                  <Input type="number" value={d.recurInterval} onChange={(e) => set('recurInterval', num(e.target.value, 1))} disabled={!!d.recurCron.trim()} />
                </div>
                <div className="col-span-2">
                  <Label>Unit</Label>
                  <Select value={d.recurUnit} onValueChange={(v) => set('recurUnit', v as ConnectivityDraft['recurUnit'])} disabled={!!d.recurCron.trim()}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {['minutes', 'hours', 'days', 'weeks', 'months'].map((u) => (
                        <SelectItem key={u} value={u}>{u}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Window length (min)</Label>
                  <Input type="number" value={d.windowDurationMin} onChange={(e) => set('windowDurationMin', num(e.target.value, 20))} />
                </div>
                <div>
                  <Label>Keep ahead (days)</Label>
                  <Input type="number" value={d.horizonDays} onChange={(e) => set('horizonDays', num(e.target.value, 7))} />
                </div>
              </div>
              <div>
                <Label>Cron (optional, overrides the interval)</Label>
                <Input value={d.recurCron} onChange={(e) => set('recurCron', e.target.value)} placeholder="0 2 * * 1-5" />
                <p className="text-caption text-muted-foreground mt-1">
                  Evaluated in your timezone and DST-safe, so "02:00 on weekdays" stays 02:00 across a clock change.
                </p>
              </div>
            </div>
          )}

          {d.scheduleKind === 'windows' && (
            <div>
              <Label>Windows (start,end[,satelliteId] — one per line)</Label>
              <Textarea
                rows={4}
                value={d.windowsCsv}
                onChange={(e) => set('windowsCsv', e.target.value)}
                placeholder={'2026-10-01T03:12:00Z,2026-10-01T03:21:00Z,SAT-1'}
              />
              <p className="text-caption text-muted-foreground mt-1">
                The device only ever sees the next 48 hours, so top these up before they run out —
                the run warns you when they are about to.
              </p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Scan interval (s)</Label>
              <Input type="number" value={d.scanIntervalSec} onChange={(e) => set('scanIntervalSec', num(e.target.value, 60))} />
            </div>
            <div>
              <Label>Attach policy</Label>
              <Select value={d.attachPolicy} onValueChange={(v) => set('attachPolicy', v as ConnectivityDraft['attachPolicy'])}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="on_detect">When the target is seen</SelectItem>
                  <SelectItem value="always_in_window">Always, inside a window</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Hold after attach (s)</Label>
              <Input type="number" value={d.holdSec} onChange={(e) => set('holdSec', num(e.target.value, 300))} />
            </div>
            <div>
              <Label>Between windows</Label>
              <Select value={d.idlePolicy} onValueChange={(v) => set('idlePolicy', v as ConnectivityDraft['idlePolicy'])}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="detached">Stay detached</SelectItem>
                  <SelectItem value="terrestrial_auto">Fall back to any network</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </>
      )}

      <div>
        <Label>Run a test on attach</Label>
        <Select value={d.onAttachTestConfigId || 'none'} onValueChange={(v) => set('onAttachTestConfigId', v === 'none' ? '' : v)}>
          <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">None</SelectItem>
            {configs.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <p className="text-caption text-muted-foreground mt-1">
          Fires each time a modem attaches. Those runs list under this one.
        </p>
      </div>

      <div>
        <Label>Capture</Label>
        <div className="flex items-center gap-4 mt-1">
          <div className="flex items-center gap-2">
            <Switch checked={d.pcap} onCheckedChange={(v) => set('pcap', v)} />
            <Label>pcap</Label>
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={d.diagEnabled} onCheckedChange={(v) => set('diagEnabled', v)} />
            <Label>L3 trace</Label>
          </div>
          <div className="flex-1">
            <Select value={d.diagMask} onValueChange={(v) => set('diagMask', v as ConnectivityDraft['diagMask'])}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="signaling">signaling</SelectItem>
                <SelectItem value="full">full</SelectItem>
                <SelectItem value="deep">deep</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div>
        <Label>Stop</Label>
        <div className="grid grid-cols-2 gap-3 mt-1">
          <Select value={d.endMode} onValueChange={(v) => set('endMode', v as ConnectivityDraft['endMode'])}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="until_stopped">When I stop it</SelectItem>
              <SelectItem value="after">After a duration</SelectItem>
              <SelectItem value="at">At a time</SelectItem>
            </SelectContent>
          </Select>
          {d.endMode === 'after' && (
            <div>
              <Label>Hours</Label>
              <Input type="number" value={d.durationHours} onChange={(e) => set('durationHours', num(e.target.value, 24))} />
            </div>
          )}
          {d.endMode === 'at' && (
            <div>
              <Label>Ends at</Label>
              <Input type="datetime-local" value={d.endsAtLocal} onChange={(e) => set('endsAtLocal', e.target.value)} />
            </div>
          )}
        </div>
      </div>

      <div className="rounded-md bg-muted/50 p-3">
        <Badge variant="secondary">No tokens</Badge>
        <span className="text-caption text-muted-foreground ml-2">
          This runs on your own edge hardware, so its modem time is not metered.
        </span>
      </div>
    </div>
  );
};

export default ConnectivityFields;
